import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import MenuDrawer from './components/MenuDrawer';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { fetchWithLanguage } from './utils/api';
import './App.css';

// Lazy loaded components
const MasterList = lazy(() => import('./components/MasterList'));
const MasterDetail = lazy(() => import('./components/MasterDetail'));
const BookingsList = lazy(() => import('./components/BookingsList'));
const MasterBookingsList = lazy(() => import('./components/MasterBookingsList'));
const PendingBookingsList = lazy(() => import('./components/PendingBookingsList'));
const ScheduleManagement = lazy(() => import('./components/ScheduleManagement'));
const ServiceManagement = lazy(() => import('./components/ServiceManagement'));

// Интерфейс для типа пользователя
interface User {
  isAuthenticated: boolean;
  phone: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  is_master?: boolean;
}

// Основной компонент домашней страницы
const HomePage = () => {
  const { t } = useLanguage();
  
  return (
    <div className="home-page">
      <h2>{t('welcome')}</h2>
      <p>{t('findMasters')}</p>
      <div className="home-actions">
        <Link to="/masters" className="button primary-button">
          {t('goToMasters')}
        </Link>
      </div>
    </div>
  );
};

// Компонент с маршрутами и разметкой приложения
const AppContent = () => {
  const { language, t } = useLanguage();
  const basename = language ? `/${language}` : '';
  const [user, setUser] = useState<User | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  
  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  // Проверка аутентификации пользователя при загрузке
  useEffect(() => {
    fetchUserInfo();
  }, []);

  // Получение информации о пользователе
  const fetchUserInfo = async () => {
    try {
      const data = await fetchWithLanguage('/api/auth/user');
      
      if (data.isAuthenticated) {
        // Determine if user is a master based on is_master field
        setUser({
          ...data, 
        });
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Error fetching user info:', error);
      setUser(null);
    }
  };

  // Обработчик успешного входа
  const handleLogin = (userData: User) => {
    // After login, fetch the complete user profile
    fetchUserInfo();
  };

  // Обработчик выхода
  const handleLogout = () => {
    setUser(null);
  };

  return (
    <Router basename={basename}>
      <div className="App">
        <MenuDrawer 
          isOpen={menuOpen}
          onClose={() => setMenuOpen(false)}
          user={user}
          language={language}
          onLogin={handleLogin}
          onLogout={handleLogout}
        />
        <header className="App-header">
          <div className="header-left">
            <button className="burger-menu-button" onClick={toggleMenu}>
              ☰
            </button>
            <h1>{t('appTitle')}</h1>
          </div>
          <div className="header-controls">
            {/* Элементы управления перемещены в меню */}
          </div>
        </header>
        <main>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/masters" element={
              <Suspense fallback={<div>{t('loading') || 'Loading...'}</div>}>
                <div>
                  <h2>{t('mastersList')}</h2>
                  <MasterList />
                </div>
              </Suspense>
            } />
            <Route path="/masters/:id" element={
              <Suspense fallback={<div>{t('loading') || 'Loading...'}</div>}>
                <MasterDetail />
              </Suspense>
            } />
            <Route path="/bookings" element={
              <Suspense fallback={<div>{t('loading') || 'Loading...'}</div>}>
                <BookingsList />
              </Suspense>
            } />
            <Route path="/pending-orders" element={
              user && user.is_master ? (
                <Suspense fallback={<div>{t('loading') || 'Loading...'}</div>}>
                  <PendingBookingsList />
                </Suspense>
              ) : (
                <div>{t('accessDenied') || 'Access Denied'}</div>
              )
            } />
            <Route path="/master-bookings" element={
              user && user.is_master ? (
                <Suspense fallback={<div>{t('loading') || 'Loading...'}</div>}>
                  <MasterBookingsList />
                </Suspense>
              ) : (
                <div>{t('accessDenied') || 'Access Denied'}</div>
              )
            } />
            <Route path="/schedule" element={
              user && user.is_master ? (
                <Suspense fallback={<div>{t('loading') || 'Loading...'}</div>}>
                  <ScheduleManagement />
                </Suspense>
              ) : (
                <div>{t('accessDenied') || 'Access Denied'}</div>
              )
            } />
            <Route path="/services" element={
              user && user.is_master ? (
                <Suspense fallback={<div>{t('loading') || 'Loading...'}</div>}>
                  <ServiceManagement />
                </Suspense>
              ) : (
                <div>{t('accessDenied') || 'Access Denied'}</div>
              )
            } />
            <Route path="/schedule" element={
              user && user.is_master ? (
                <Suspense fallback={<div>{t('loading') || 'Loading...'}</div>}>
                  <ScheduleManagement />
                </Suspense>
              ) : (
                <div>{t('accessDenied') || 'Access Denied'}</div>
              )
            } />
            <Route path="*" element={<div>{t('pageNotFound')}</div>} />
          </Routes>
        </main>
      </div>
    </Router>
  );
};

// Корневой компонент с провайдером языка
function App() {
  return (
    <LanguageProvider>
      <AppContent />
    </LanguageProvider>
  );
}

export default App;
